// Components
import Heading from '@ramsey-design-system/heading';
import Tag from '@ramsey-design-system/tag';
import Text from '@ramsey-design-system/text';
import Button from '@ramsey-design-system/button';

// Hooks
import { useEveryDollarTrialEligibility } from '../../hooks/useEveryDollarTrialEligibility';
import { useHasFpuAllAccess } from '~fpu/shared/hooks/useHasFpuAllAccess';
import { useEveryDollarTrialActivation } from '../../hooks/useEveryDollarTrialActivation';

// Utils
import storeConfig from '~fpu/config/storeConfig';
import { analyticsLogger } from '~fpu/shared/utils/analytics';

type PremiumFeatureKey =
  | 'Connect Your Bank'
  | 'Set Up Paycheck Planning'
  | 'Create a Goal'
  | 'Check Out Insights';

const CONNECT_YOUR_BANK: PremiumFeatureKey = 'Connect Your Bank';
const SET_UP_PAYCHECK_PLANNING: PremiumFeatureKey = 'Set Up Paycheck Planning';
const CREATE_A_GOAL: PremiumFeatureKey = 'Create a Goal';
const CHECK_OUT_INSIGHTS: PremiumFeatureKey = 'Check Out Insights';

const PREMIUM_FEATURE_LINKS: Record<PremiumFeatureKey, string> = {
  [CONNECT_YOUR_BANK]: 'https://everydollar.onelink.me/7YvT/f44e0a1f',
  [SET_UP_PAYCHECK_PLANNING]: 'https://everydollar.onelink.me/7YvT/qe32g8j0',
  [CREATE_A_GOAL]: `${storeConfig.everydollarWeb}/goals`,
  [CHECK_OUT_INSIGHTS]: 'https://everydollar.onelink.me/7YvT/f3b27cb0'
};

function PremiumFeatures() {
  const { data } = useEveryDollarTrialEligibility();

  if (!data) {
    return null;
  }

  return (
    <div className="rounded-lg bg-white p-4 pt-3 shadow-sm">
      <PremiumFeaturesHeading />
      <PremiumFeatureItem
        heading="Bank Connect"
        text="When you connect your bank, your transactions will stream in automatically."
        image="https://cdn.ramseysolutions.net/media/ramsey-plus/app/budget/everydollar-connect.svg"
        label={CONNECT_YOUR_BANK}
      />

      <PremiumFeatureItem
        heading="Paycheck Planning"
        text="If you have an irregular income, then paycheck planning is the best way to manage your day-to-day budget and pace yourself for the month."
        image="https://cdn.ramseysolutions.net/media/ramsey-plus/app/budget/everydollar-planning.svg"
        label={SET_UP_PAYCHECK_PLANNING}
      />

      <PremiumFeatureItem
        heading="Goals"
        text="Set up, track and make progress on your financial goals. (Web only)"
        image="https://cdn.ramseysolutions.net/media/ramsey-plus/app/budget/everydollar-goals.svg"
        label={CREATE_A_GOAL}
      />

      <PremiumFeatureItem
        heading="Insights"
        text="Check out your custom reports on your income and spending."
        image="https://cdn.ramseysolutions.net/media/ramsey-plus/app/budget/everydollar-insights.svg"
        label={CHECK_OUT_INSIGHTS}
      />
    </div>
  );
}

function PremiumFeaturesHeading() {
  const { data } = useEveryDollarTrialEligibility();
  const { data: hasFpuAllAccess } = useHasFpuAllAccess();

  if (!data) {
    return null;
  }

  const hasPremiumAccess =
    data.isEveryDollarPremiumSubscriber || hasFpuAllAccess;
  const isEligibleForTrial = data.isEligibleForTrial && !hasPremiumAccess;

  let headingText = <>Get the premium features of EveryDollar!</>;

  if (isEligibleForTrial) {
    headingText = <>You&apos;ve got EveryDollar premium for 3 months!</>;
  } else if (hasPremiumAccess) {
    headingText = <>Guess what? You have access to premium features!</>;
  }

  return (
    <Heading level="2" className="text-6 sm:text-7 mb-4">
      {headingText}
    </Heading>
  );
}

function PremiumFeatureItem({
  heading,
  text,
  image,
  label
}: {
  heading: string;
  text: string;
  image: string;
  label: PremiumFeatureKey;
}) {
  const { data } = useEveryDollarTrialEligibility();
  const { data: hasFpuAllAccess } = useHasFpuAllAccess();

  const { mutate, isPending } = useEveryDollarTrialActivation();

  if (!data) {
    return null;
  }

  const hasPremiumAccess =
    data.isEveryDollarPremiumSubscriber || hasFpuAllAccess;
  const isEligibleForTrial = data.isEligibleForTrial && !hasPremiumAccess;

  const handleActivation = (label: PremiumFeatureKey) => {
    mutate(
      { claimMethod: 'app' },
      {
        onSuccess: () => {
          analyticsLogger.trackElementClicked({
            element: 'EveryDollar Premium Activated',
            label
          });

          window.open(PREMIUM_FEATURE_LINKS[label], '_blank');
        }
      }
    );
  };

  const handleClick = (e: React.MouseEvent, label: PremiumFeatureKey) => {
    if (isEligibleForTrial) {
      e.preventDefault();

      handleActivation(label);
    }

    analyticsLogger.trackElementClicked({ element: 'Budget', label });
  };

  let tagText = 'Premium Feature';

  if (isEligibleForTrial || hasPremiumAccess) {
    tagText = 'Included With Premium';
  }

  return (
    <div className="border-sm border-gray-30 my-2 flex justify-between rounded-md p-3 sm:px-4">
      <div className="max-w-52">
        <div className="flex flex-wrap items-center">
          <Heading level="3" className="text-5 mr-1">
            {heading}
          </Heading>
          <Tag color="positive" subtle size="sm" data-testid="premium-tag">
            {tagText}
          </Tag>
        </div>

        <Text className="text-3 mt-1 mb-3">{text}</Text>

        <Button
          {...(!isEligibleForTrial && {
            element: 'a',
            target: '_blank',
            href: PREMIUM_FEATURE_LINKS[label]
          })}
          appearance="ghost"
          loading={isPending}
          onClick={(e: React.MouseEvent) => handleClick(e, label)}
        >
          {label}
        </Button>
      </div>
      <img src={image} alt="" className="mr-2 hidden sm:block" />
    </div>
  );
}

export default PremiumFeatures;
